/**
 * The orb, made into something you can actually click.
 *
 * `OrbCanvas` is a renderer: it takes a state and a level getter and draws. It has
 * no opinion about calls, no button, no status text and no placement — which is
 * exactly right for a primitive that the flag and the plain button both embed
 * inside themselves.
 *
 * This is the thin layer that turns it into a widget in its own right: it maps a
 * `VoiceControl` phase onto the renderer's state, wraps it in a real button, adds a
 * one-line status, and optionally floats it in a corner. Small enough that it could
 * have lived in the playground page, and it does not because the EMBED needs it too
 * — `orb` is one of the five installable variants, and a variant that only exists
 * inside one page's file cannot be one.
 */
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { OrbCanvas, type OrbCanvasTheme, type OrbState } from './orb-canvas'
import { isLive, type VoiceControl, type VoicePlacement, type VoiceWidgetCommon } from './types'
import { withTakeover } from './stage-text'

export interface OrbWidgetProps extends VoiceWidgetCommon {
  control: VoiceControl
  /** Diameter in CSS pixels. Default 112. */
  size?: number
  /** `inline` renders in flow. Anything else pins it to that corner. */
  placement?: VoicePlacement
  /** Which orb look to render: `cloud` (default), `circle`, or `radial`. Named
   *  `orbTheme` because `VoiceWidgetCommon.theme` is already the light/dark
   *  scheme. Rides the embed tag as `data-orb-theme`. */
  orbTheme?: OrbCanvasTheme
  /** Wins over the page's z-index. See the note on the constant. */
  zIndex?: number
  className?: string
}

/**
 * High enough to sit above a host page's own chrome, low enough to stay inside the
 * 32-bit signed range that every engine accepts.
 *
 * Sticky headers commonly use 100–10000; cookie banners reach for 999999. This runs
 * on pages we have never seen, so the default has to win — and it is a prop because
 * the one thing it must NOT cover is the host's own modal, and only they know.
 */
const DEFAULT_Z = 2147483000

/** Where each corner sits, and how far from the edge. 24px clears a typical
 *  scrollbar gutter and reads as deliberate rather than cramped. */
const CORNERS: Record<Exclude<VoicePlacement, 'inline'>, string> = {
  'bottom-right': 'bottom:24px;right:24px;',
  'bottom-left': 'bottom:24px;left:24px;',
  'top-right': 'top:24px;right:24px;',
  'top-left': 'top:24px;left:24px;',
  // A bare side has no vertical anchor of its own, so it centres. The flag owns
  // the edge-clamped look; an orb pinned to the middle of an edge just floats.
  left: 'top:50%;left:24px;transform:translateY(-50%);',
  right: 'top:50%;right:24px;transform:translateY(-50%);',
}

/**
 * Phase → renderer state.
 *
 * A near-identity mapping, which is deliberate: the two unions were designed
 * together so that this function is boring. `ended` returns to `idle` because the
 * widget is immediately reusable — a visitor who hangs up and wants to ask one more
 * thing should see an inviting orb, not a finished one. That now happens the
 * INSTANT the end button is pressed, while the call is still draining: there is no
 * `ending` phase to render any more (see `VoicePhase`).
 */
function orbStateFor(control: VoiceControl): OrbState {
  switch (control.phase) {
    case 'connecting':
      return 'connecting'
    case 'listening':
      return 'listening'
    case 'thinking':
      return 'thinking'
    case 'speaking':
      return 'speaking'
    case 'error':
      return 'error'
    case 'idle':
    case 'ended':
      return 'idle'
  }
}

/**
 * What the orb says about itself, in one short line.
 *
 * IT NEVER SAYS `endLabel`, and it used to look as though it might. The call site
 * passed `endLabel` while live and the literal `"Try again"` otherwise, and the only
 * branch that read the argument was `error` — which is not a live phase, so the
 * `endLabel` copy was unreachable and the literal was the only value that ever
 * arrived. One parameter fewer, one fewer place a reader has to work out that this
 * widget's visible line is written from the PHASE and not from the operator's text.
 */
function statusFor(control: VoiceControl, label: string): string {
  switch (control.phase) {
    case 'idle':
    case 'ended':
      return label
    case 'connecting':
      return 'Connecting…'
    case 'listening':
      return 'Listening'
    // "Talk to interrupt" rather than "Speaking": it tells the visitor the one thing
    // they cannot discover on their own, which is that barging in works.
    case 'speaking':
      return 'Talk to interrupt'
    case 'thinking':
      return 'Thinking…'
    case 'error':
      return 'Try again'
  }
}

export function OrbWidget({
  control,
  size = 112,
  placement = 'inline',
  orbTheme,
  zIndex = DEFAULT_Z,
  className,
  label = 'Tap to talk',
  /**
   * THE BUTTON'S NAME WHILE LIVE, AND NOTHING ELSE — no pixel of the orb is drawn
   * from it (the line under the orb is written from the phase; see `statusFor`). The
   * config editor stopped offering it on 2026-08-25 for exactly that reason: two text
   * fields for the two halves of one icon button's accessible name is a decision with
   * no visible outcome. The prop and this default stay, so a hand-written tag with
   * `data-end-label` still reaches a screen reader in the operator's own language.
   */
  endLabel = 'Tap to end',
  caption,
  accent,
  onAccent,
}: OrbWidgetProps) {
  const live = isLive(control.phase)
  const busy = control.phase === 'connecting'
  const status = withTakeover(control, statusFor(control, label))

  /**
   * A 1 Hz tick, only while live, only to redraw the elapsed time.
   *
   * Not `startedAtMs` in a `useEffect` dependency and not a per-frame value: one
   * interval that exists for as long as the timer is on screen. `control.startedAtMs`
   * is the source of truth, so a re-render from any other cause still shows the right
   * number.
   */
  const [, tick] = useState(0)
  useEffect(() => {
    if (!live || control.startedAtMs === null) return
    const id = setInterval(() => tick((n) => n + 1), 1000)
    return () => clearInterval(id)
  }, [live, control.startedAtMs])

  const elapsed =
    live && control.startedAtMs !== null ? formatElapsed(Date.now() - control.startedAtMs) : null

  // Kept in a ref so the click handler is stable and cannot capture a stale phase.
  const phaseRef = useRef(control.phase)
  phaseRef.current = control.phase

  const body = (
    <div className={cn('flex flex-col items-center gap-3', className)}>
      <button
        type="button"
        onClick={() => (isLive(phaseRef.current) ? control.stop() : control.start())}
        disabled={busy}
        aria-label={live ? endLabel : label}
        className={cn(
          'relative rounded-full outline-none transition-transform duration-200',
          // A ring rather than an outline, so it follows the circle. `focus-visible`
          // only, because a mouse click on a big round target does not need a ring.
          'focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-current',
          busy ? 'cursor-wait' : 'cursor-pointer hover:scale-[1.03] active:scale-[0.98]',
          // No `motion-safe:` prefix here: the transform is 20ms of scale on a
          // deliberate press, which is not the kind of motion the preference is about.
        )}
        style={{ width: size, height: size, ...(accent === undefined ? {} : { color: accent }) }}
      >
        <OrbCanvas
          state={orbStateFor(control)}
          size={size}
          levels={control.levels}
          {...(orbTheme === undefined ? {} : { theme: orbTheme })}
          {...(accent === undefined || onAccent === undefined
            ? {}
            : { colors: { from: accent, to: onAccent } })}
        />
      </button>

      <div className="flex flex-col items-center gap-0.5 text-center">
        {/* Polite, not assertive: a status that interrupts a screen reader mid-word
            every time the agent starts talking is worse than no status at all. */}
        <span aria-live="polite" className="text-sm font-medium">
          {status}
        </span>
        {elapsed !== null && <span className="text-xs tabular-nums opacity-60">{elapsed}</span>}
        {/* px, not rem: inside the embed's shadow root rem tracks the HOST page's
            root font-size. 352px ≡ the old 22rem, 10.4px ≡ the old 0.65rem. */}
        {control.error !== null && (
          <span className="max-w-[352px] text-xs text-destructive">{control.error}</span>
        )}
        {caption !== undefined && caption !== '' && (
          <span className="text-[10.4px] font-light opacity-50">{caption}</span>
        )}
      </div>
    </div>
  )

  if (placement === 'inline') return body
  return (
    // `position: fixed` written as a style string rather than Tailwind classes, so
    // the whole rule survives being bundled into a shadow root where the utility
    // layer may have been tree-shaken differently. See the note in embed.css.
    <div style={cssText(`position:fixed;${CORNERS[placement]}z-index:${zIndex};`)}>{body}</div>
  )
}

/** `style` as a parsed object, because React will not take a string. Written this
 *  way so the CORNERS table reads as CSS. */
function cssText(text: string): React.CSSProperties {
  const out: Record<string, string> = {}
  for (const rule of text.split(';')) {
    const at = rule.indexOf(':')
    if (at < 0) continue
    const prop = rule.slice(0, at).trim().replace(/-([a-z])/g, (_, c: string) => c.toUpperCase())
    out[prop] = rule.slice(at + 1).trim()
  }
  return out as React.CSSProperties
}

function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}
